import type {
    CSSProperties,
    FC,
    MouseEvent as ReactMouseEvent,
    PointerEvent as ReactPointerEvent,
    ReactNode
} from 'react'
import type { KeyDef } from '@/constants/keyboard'

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { playKeySound } from '@/lib/typing-sound'
import { HyperStar } from '@/components/landing/brand-marks'

import {
    KEYBOARD_ROWS,
    KEY_BY_CODE,
    KEY_HUE,
    KEY_PLACEMENTS,
    LED_COLORS,
    ROW_SHADE
} from '@/constants/keyboard'

interface KeyboardCrop {
    rows?: [number, number]
    columns?: [number, number]
}

interface KeyboardProps {
    unit: string
    tilt?: number
    perspective?: number
    origin?: [number, number]
    variant?: 'default' | 'glow'
    legends?: boolean
    interactive?: boolean
    lighting?: boolean
    lifted?: string
    crop?: KeyboardCrop
    label?: string
    className?: string
    style?: CSSProperties
}

interface KeyCapProps {
    def: KeyDef
    visible: boolean
    pressed: boolean
    hovered: boolean
    lit: boolean
    lifted: boolean
    legends: boolean
    knobTurns: number
}

const EMPTY_SET: ReadonlySet<string> = new Set()
const KNOB_DISCS = [0.35, 0.75, 1.15, 1.55, 1.9]
const STEM_DISCS = [0.32, 0.36, 0.4, 0.43]
const GLOW_PLACEMENTS = KEY_PLACEMENTS.filter(placement => placement.id !== 'knob')

const units = (value: number): string => `calc(var(--u) * ${Number(value.toFixed(3))})`

const isTypingTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false

    return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

const usePressedKeys = (enabled: boolean): ReadonlySet<string> => {
    const [pressed, setPressed] = useState<ReadonlySet<string>>(EMPTY_SET)

    useEffect(() => {
        if (!enabled) return

        const handleDown = (event: KeyboardEvent) => {
            if (isTypingTarget(event.target)) return

            const id = KEY_BY_CODE.get(event.code)

            if (!id) return

            setPressed(previous => (previous.has(id) ? previous : new Set(previous).add(id)))
        }

        const handleUp = (event: KeyboardEvent) => {
            if (event.key === 'Meta') {
                setPressed(EMPTY_SET)
                return
            }

            const id = KEY_BY_CODE.get(event.code)

            if (!id) return

            setPressed(previous => {
                if (!previous.has(id)) return previous

                const next = new Set(previous)
                next.delete(id)

                return next
            })
        }

        const reset = () => setPressed(EMPTY_SET)

        window.addEventListener('keydown', handleDown)
        window.addEventListener('keyup', handleUp)
        window.addEventListener('blur', reset)

        return () => {
            window.removeEventListener('keydown', handleDown)
            window.removeEventListener('keyup', handleUp)
            window.removeEventListener('blur', reset)
        }
    }, [enabled])

    return pressed
}

const Legend: FC<{ def: KeyDef }> = ({ def }): ReactNode => {
    const Icon = def.icon

    switch (def.variant) {
        case 'alpha':
            return (
                <span>
                    {def.label}
                </span>
            )
        case 'pair':
            return (
                <span className='kb-legend-pair'>
                    <span>
                        {def.top}
                    </span>

                    <span>
                        {def.label}
                    </span>
                </span>
            )
        case 'mod':
            return (
                <span className='kb-legend-mod' data-italic={def.italic ? 'true' : undefined}>
                    {def.label}
                </span>
            )
        case 'fn':
            return (
                <>
                    {Icon && <Icon className='kb-icon' strokeWidth={2.25} aria-hidden='true' />}

                    <span className='kb-legend-fn'>
                        {def.label}
                    </span>
                </>
            )
        case 'icon':
            return Icon ? <Icon className='kb-icon' strokeWidth={2} aria-hidden='true' /> : null
        case 'hyper':
            return <HyperStar className='kb-icon' />
        default:
            return null
    }
}

const Lightbar: FC<{ side: 'left' | 'right' }> = ({ side }): ReactNode => (
    <span className='kb-lightbar' data-side={side}>
        {LED_COLORS.map((color, index) => (
            <span
                key={index}
                className='kb-led'
                style={{ '--led': color, '--i': index } as CSSProperties}
            />
        ))}
    </span>
)

const Knob: FC<{ turns: number }> = ({ turns }): ReactNode => (
    <div className='kb-knob' data-key='knob'>
        {KNOB_DISCS.map(z => (
            <span
                key={z}
                className='kb-knob-disc'
                style={{ '--z': z } as CSSProperties}
            />
        ))}

        <div className='kb-knob-top'>
            <div
                className='kb-knob-dial'
                style={{ '--turn': turns } as CSSProperties}
            />
        </div>
    </div>
)

const LiftedParts: FC = (): ReactNode => (
    <>
        <div className='kb-socket'>
            <span className='kb-socket-led' />
            <span className='kb-socket-hole' style={{ left: '20%', top: '50%' }} />
            <span className='kb-socket-hole' style={{ left: '62%', top: '64%' }} />
        </div>

        <div className='kb-switch'>
            <div className='kb-switch-top' />
            <div className='kb-switch-front' />

            <div className='kb-stem'>
                {STEM_DISCS.map(z => (
                    <span
                        key={z}
                        className='kb-stem-disc'
                        style={{ '--z': z } as CSSProperties}
                    />
                ))}

                <span className='kb-stem-top' />
            </div>
        </div>
    </>
)

const KeyCap = memo(({ def, visible, pressed, hovered, lit, lifted, legends, knobTurns }: KeyCapProps): ReactNode => {
    const style = { '--w': def.w ?? 1, '--h': KEY_HUE.get(def.id) ?? 200 } as CSSProperties

    if (!visible) return <div className='kb-key' style={style} />

    if (def.variant === 'knob') {
        return (
            <div className='kb-key' data-variant='knob' style={style}>
                <Knob turns={knobTurns} />
            </div>
        )
    }

    return (
        <div
            className='kb-key'
            data-variant={def.variant}
            data-textured={def.textured ? 'true' : undefined}
            style={style}
        >
            {lifted && <LiftedParts />}

            <div
                className='kb-cap'
                data-key={def.id}
                data-pressed={pressed ? 'true' : undefined}
                data-hover={hovered ? 'true' : undefined}
                data-lit={lit ? 'true' : undefined}
                data-lifted={lifted ? 'true' : undefined}
            >
                <div className='kb-cap-top'>
                    <div className='kb-cap-face'>
                        {legends && <Legend def={def} />}
                        {legends && def.bump && <span className='kb-bump' />}
                    </div>
                </div>

                <span className='kb-cap-front' />
            </div>
        </div>
    )
})

KeyCap.displayName = 'KeyCap'

const Keyboard: FC<KeyboardProps> = ({
    unit,
    tilt = 0,
    perspective = 36,
    origin,
    variant = 'default',
    legends = true,
    interactive = false,
    lighting = false,
    lifted,
    crop,
    label,
    className,
    style
}): ReactNode => {
    const typedKeys = usePressedKeys(interactive)
    const [pointerKey, setPointerKey] = useState<string | null>(null)
    const [knobTurns, setKnobTurns] = useState(0)
    const [hoverKey, setHoverKey] = useState<string | null>(null)
    const pointerTypeRef = useRef('mouse')
    const hoverRef = useRef<string | null>(null)

    const litKeys = useMemo(() => {
        const keys = new Set(typedKeys)

        if (hoverKey) keys.add(hoverKey)
        if (pointerKey) keys.add(pointerKey)

        return keys
    }, [typedKeys, hoverKey, pointerKey])

    const pivot = origin ? `calc(var(--u) * ${origin[0]}) calc(var(--u) * ${origin[1]})` : '50% 50%'

    useEffect(() => {
        if (!interactive) return

        const release = () => setPointerKey(null)

        window.addEventListener('pointerup', release)
        window.addEventListener('pointercancel', release)

        return () => {
            window.removeEventListener('pointerup', release)
            window.removeEventListener('pointercancel', release)
        }
    }, [interactive])

    const pressFromEvent = useCallback((target: EventTarget | null): string | null => {
        if (!(target instanceof Element)) return null

        const id = target.closest<HTMLElement>('[data-key]')?.dataset.key

        if (!id) return null

        if (id === 'knob') {
            setKnobTurns(turns => turns + 1)
            playKeySound({ intensity: 0.35, pitch: 1.8 })

            return null
        }

        setPointerKey(id)
        playKeySound({ space: id === 'space', pitch: 0.92 + Math.random() * 0.16 })

        return id
    }, [])

    const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        pointerTypeRef.current = event.pointerType

        if (event.pointerType === 'touch') return

        pressFromEvent(event.target)
    }

    const updateHover = (id: string | null) => {
        if (hoverRef.current === id) return

        hoverRef.current = id
        setHoverKey(id)
    }

    const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'touch') return

        const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-key]') : null
        const id = target?.dataset.key

        updateHover(id && id !== 'knob' ? id : null)
    }

    const handleClick = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (pointerTypeRef.current !== 'touch') return

        const id = pressFromEvent(event.target)

        if (id) window.setTimeout(() => setPointerKey(current => (current === id ? null : current)), 140)
    }

    const stageStyle = {
        '--u': unit,
        width: 'calc(var(--u) * 16.8)',
        perspective: `calc(var(--u) * ${perspective})`,
        perspectiveOrigin: pivot,
        ...style
    } as CSSProperties

    return (
        <div
            className={cn('relative', className)}
            style={stageStyle}
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
        >
            <div
                className='kb'
                data-variant={variant}
                data-interactive={interactive ? 'true' : undefined}
                data-lighting={lighting ? 'true' : undefined}
                style={{ transform: tilt ? `rotateX(${tilt}deg)` : undefined, transformOrigin: pivot }}
            >
                <div className='kb-case'>
                    <span className='kb-case-front' />
                    <Lightbar side='left' />
                    <Lightbar side='right' />

                    <div
                        className='kb-plate'
                        onPointerDown={interactive ? handlePointerDown : undefined}
                        onPointerMove={interactive ? handlePointerMove : undefined}
                        onPointerLeave={interactive ? () => updateHover(null) : undefined}
                        onClick={interactive ? handleClick : undefined}
                    >
                        {interactive && (
                            <div className='kb-glow-layer' aria-hidden='true'>
                                {GLOW_PLACEMENTS.map(placement => (
                                    <span
                                        key={placement.id}
                                        className='kb-glow'
                                        data-lit={litKeys.has(placement.id) ? 'true' : undefined}
                                        style={{
                                            '--h': placement.hue,
                                            left: units(placement.x - 1),
                                            top: units(placement.row - 1.1),
                                            width: units(placement.w + 2),
                                            height: units(3.2)
                                        } as CSSProperties}
                                    />
                                ))}
                            </div>
                        )}

                        {KEYBOARD_ROWS.map((row, rowIndex) => {
                            const rowStyle = lighting ? { '--shade': ROW_SHADE[rowIndex] ?? 0 } as CSSProperties : undefined

                            if (crop?.rows && (rowIndex < crop.rows[0] || rowIndex > crop.rows[1])) {
                                return <div key={rowIndex} className='kb-row' data-row={rowIndex} />
                            }

                            let column = 0

                            return (
                                <div key={rowIndex} className='kb-row' data-row={rowIndex} style={rowStyle}>
                                    {row.map(key => {
                                        const start = column
                                        const width = key.w ?? 1

                                        column += width

                                        const visible = !crop?.columns || (start + width > crop.columns[0] && start < crop.columns[1])

                                        return (
                                            <KeyCap
                                                key={key.id}
                                                def={key}
                                                visible={visible}
                                                pressed={typedKeys.has(key.id) || pointerKey === key.id}
                                                hovered={hoverKey === key.id}
                                                lit={litKeys.has(key.id)}
                                                lifted={lifted === key.id}
                                                legends={legends}
                                                knobTurns={key.variant === 'knob' ? knobTurns : 0}
                                            />
                                        )
                                    })}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Keyboard
